import { DateTime } from 'luxon';
import React, { useCallback, useState, useEffect } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { filtersTypes } from 'app-components/flux/filters';
import { numberUtils } from 'app-components/utils/number';
import RolloverToggle from 'components/Dialogs/EditPlannedSpendingSeries/RolloverToggle';
import StdDialog from 'components/Dialogs/StdDialog';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import CategoryField from 'components/QuickenControls/CategoryField';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuidv4 } from 'uuid';

import Box from '@mui/material/Box';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { freeToSpendActions, freeToSpendSelectors, freeToSpendTypes } from 'app-components/flux/free-to-spend';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import QIllustration from 'components/QIllustration';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

const categoryWidth = '100%';
const useStyles = makeStyles()((theme) => ({
  amountField: {
    textAlign: 'left !important',
    width: '40%',
  },
  nameField: {
    width: '60%',
    marginRight: theme.spacing(2),
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 24,
    paddingTop: 24,
  },
  suggested: {
    padding: 8,
    borderRadius: 12,
    width: categoryWidth,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    marginTop: 8,
    marginBottom: 8,
  },
  warn: {
    padding: 12,
    borderRadius: 12,
    backgroundColor: theme.palette.warning.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    opacity: 0,
    marginRight: 64,
  },
  statusIcon: {
    height: 32,
    width: 32,
    marginRight: 8,
    marginTop: 1,
    color: theme.palette.warning.main,
    alignItems: 'flex-start',
  },
  show: {
    opacity: '1 !important',
  },
  textColor: {
    color: theme.palette.info.dark,
  },
  catFieldWrapper: {
    width: '100%',
    outline: 'none',
  },
  iconCat: {
    height: 30,
    width: 30,
    paddingRight: 12,
    filter: theme.palette.categoryIcon.filter,
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: theme.spacing(2),
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '75%',
  },
  dialogBackground: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.paper,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  radioWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  radioGroup: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(1),
  },
  bottomContent: {
    width: '75%',
    flexDirection: 'column',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}));

const filterFn = (c) =>
  c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
  (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY &&
    (categoriesSelectors.isExpenseCat(null, c.id) || c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED)); // only expense categories or uncat

function AddPlannedSpending(props) {
  const {
    freeToSpendId,
    plannedItems,
    startDate,
    calculatedPlannedSpendingAmount,
    leftToSpendAmount,
    initialId,
    callback,
  } = props;

  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const currentDate = useSelector(dateSelectors.getCurrentDate);
  const pastCutoff = currentDate.startOf('month').minus({ months: 6 }).toSeconds();
  const currCutoff = currentDate.startOf('month').minus({ months: 1 }).toSeconds();

  // state inits
  const [resetRolloverAmount, setResetRolloverAmount] = useState(0);
  const [isRolledOver, setIsRolledOver] = useState(false);
  const [name, setName] = useState('');
  const [targetAmount, setTarget] = useState(0);
  const [view, setView] = useState('monthly');
  const todayDate = currentDate.startOf('month').toISODate();

  const [
    validate,
    coa,
    coaSetter,
    errorText,
    currCategory,
    _category,
    isAlreadyTracked,
    children,
    alreadyTrackedMessage,
  ] = usePlannedCategoryChecker({ startDate, isEdit: false, id: initialId });
  const isDisabled = coa === '' || coa?.id === undefined || targetAmount === null || targetAmount <= 0 || !!errorText;

  const { averageSpending } = useSelector((state) =>
    freeToSpendSelectors.getAverageSpendingForCategory(state, { coaId: coa?.id, pastCutoff, currCutoff }),
  );
  const f2sIds = useSelector((state) => freeToSpendSelectors.getFutureFreeToSpendsIds(state, { startDate }));
  const dispatch = useDispatch();
  const willCreateOneTime = coa.id && view === 'monthly' && isAlreadyTracked && !errorText;
  const canUpdateRollovers = view === 'monthly' && todayDate <= startDate && !isAlreadyTracked;

  const { classes, cx } = useStyles();

  // If we are plugging in an initial category, we need to check it for errors!
  useEffect(() => {
    if (initialId) {
      validate(coa);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item) => item !== currCategory?.knownCategoryId));
    }
  };

  // send info to qcs
  const updateF2S = () => {
    if (!targetAmount || targetAmount < 0 || !coa.id) {
      return;
    }
    const filter = [
      {
        type: 'CHARTOFACCOUNT',
        chartOfAccounts: [coa, ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? [])],
      },
    ];
    const filterItems = filtersTypes.mkFilter({
      id: null,
      clientId: uuidv4().toUpperCase(),
      type: 'FREETOSPEND',
      parentIds: f2sIds,
      filterItems: filter,
    });

    const rolloverToggled = isRolledOver && canUpdateRollovers;
    const plannedName = name === '' ? currCategory?.name || 'Uncategorized' : name;
    // Send the filterItems to the createFilter Saga and capture the returned filterID
    const plannedItem = {
      id: freeToSpendId,
      name: plannedName,
      targetAmount,
      filterItems,
      recurring: view === 'monthly' && !isAlreadyTracked,
      plannedSpendingItems: plannedItems,
      calculatedPlannedSpendingAmount,
      leftToSpendAmount,
    };

    if (canUpdateRollovers) {
      plannedItem.rolloverType = rolloverToggled
        ? freeToSpendTypes.RolloverTypeEnum.BOTH
        : freeToSpendTypes.RolloverTypeEnum.NONE;
      if (rolloverToggled) {
        plannedItem.resetRolloverAmount = resetRolloverAmount;
      }
    }

    tracker.track(tracker.events.rolloversOptIn, {
      initiator: 'create',
      value: rolloverToggled,
      category: currCategory?.name || 'Uncategorized',
    });
    if (rolloverToggled) {
      tracker.track(tracker.events.startingRollover, {
        initiator: 'create',
        sign: resetRolloverAmount >= 0 ? 'positive' : 'negative',
        amount: resetRolloverAmount,
      });
    }

    tracker.track(tracker.events.budgetItemCreate, {
      frequency: view,
      category: currCategory?.name ?? 'Uncategorized',
      amount: targetAmount,
      budget_name: name,
    });
    dispatch(freeToSpendActions.createPlannedSpending(plannedItem));
    removeCategoryFromSuggestedList();
  };

  const setPlannedName = useCallback((e) => setName(e.target.value), [setName]);

  const submit = () => {
    updateF2S();
    setName('');
    setTarget(0);
    coaSetter(null);
    if (callback) {
      callback(currCategory?.name);
    }
    dispatch(rootUiActions.removeDialog());
  };

  const formatAmount = useFormatAmount();

  const formView = (
    <div className={classes.formWrapper}>
      <div className={classes.fieldWrapper}>
        <div className={classes.row}>
          <TextField
            margin='normal'
            id='name'
            label='Name (optional)'
            inputProps={{ maxLength: 30 }}
            variant='filled'
            value={name}
            onChange={setPlannedName}
            className={classes.nameField}
            autoComplete='one-time-code'
          />
          <QAmountField
            editable
            id='target-amount'
            textFieldVariant='filled'
            label={'Target amount'}
            value={targetAmount}
            onChange={(e) => setTarget(Number(e.target.value))}
            className={classes.amountField}
            error={targetAmount < 0}
            expectedSign='positive'
            margin='normal'
          />
        </div>
        <div className={classes.row}>
          <div className={classes.catFieldWrapper}>
            <CategoryField
              variant='bodyRegular'
              id='monthly'
              editable
              width={categoryWidth}
              value={coa}
              onChange={coaSetter}
              filterFn={filterFn}
              margin='normal'
              textFieldVariant='filled'
              label='Select category'
              error={!!errorText}
              helperText={errorText}
              textFieldProps={{
                label: 'Category',
              }}
            />
          </div>
        </div>
      </div>
      <div className={classes.bottomContent}>
        <div className={cx(classes.suggested, coa.id && Math.abs(averageSpending || 0) > 0 && classes.show)}>
          {currCategory?.logoUrl && <img alt='cat-icon' src={currCategory?.logoUrl} className={classes.iconCat} />}
          <Typography component='span' variant='captionRegular' className={classes.textColor}>
            Average spending in this category each month:&nbsp;
            <Typography component='span' variant='captionMedium' className={classes.textColor}>
              {formatAmount(
                parseFloat(Math.abs(averageSpending || 0)),
                'USD',
                '0,0.00',
                numberUtils.ShowSignEnum.NEGATIVE_ONLY,
              )}
            </Typography>
          </Typography>
        </div>
        {canUpdateRollovers ? (
          <RolloverToggle
            initRolloverSetting={false}
            isRolledOver={isRolledOver}
            setIsRolledOver={setIsRolledOver}
            startDate={startDate}
            resetRolloverAmount={resetRolloverAmount}
            setResetRolloverAmount={setResetRolloverAmount}
          />
        ) : null}
      </div>
    </div>
  );

  const onHandleClose = (event, reason) => {
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      submit();
    }
    dispatch(rootUiActions.removeDialog());
  };

  const dateLabel = DateTime.fromISO(startDate).toFormat('LLL yyyy');
  return (
    <StdDialog
      onClose={onHandleClose}
      onKeyDown={(event) => {
        if (event.code === 'Enter' && !isDisabled) {
          submit();
        }
      }}
      open
      sharedcomponentid='ADD_PLANNED_SPENDING_DIALOG'
      showCloseButton
      size='md'
      title={'Add Planned Expense'}
    >
      <div className={classes.dialogBackground}>
        <div className={classes.iconContainer}>
          <Box paddingRight={1}>
            <QIllustration name='spendingPlanPlanned' size='md' />
          </Box>
          <div className={classes.radioWrapper}>
            <Typography variant='bodyRegular'>Budget your spend by category</Typography>
            {willCreateOneTime ? (
              <div className={cx(classes.warn, classes.show)}>
                {<WarningRoundedIcon className={classes.statusIcon} />}
                <Typography variant='paragraphRegular' color='textPrimary'>
                  {alreadyTrackedMessage}
                </Typography>
              </div>
            ) : (
              <RadioGroup
                className={classes.radioGroup}
                name={'include-contribution-in-spending-plan'}
                value={view}
                onChange={(e) => setView(e.target.value)}
              >
                <FormControlLabel
                  value={'monthly'}
                  control={<Radio id='monthly-toggle' />}
                  label={'Recurring expense'}
                />
                <FormControlLabel
                  value={'one-time'}
                  control={<Radio id='one-time-toggle' />}
                  label={`${dateLabel} only`}
                />
              </RadioGroup>
            )}
            {formView}
          </div>
        </div>
        <DialogActions>
          <QButton variant='contained' type='submit' disabled={isDisabled} onClick={submit} id='create-button'>
            Create
          </QButton>
        </DialogActions>
      </div>
    </StdDialog>
  );
}

AddPlannedSpending.propTypes = {
  freeToSpendId: PropTypes.string,
  plannedItems: PropTypes.object,
  startDate: PropTypes.any,
  navToPlannedSpendingOnConfirm: PropTypes.bool,
  calculatedPlannedSpendingAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  leftToSpendAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  initialId: PropTypes.string,
  callback: PropTypes.func,
};

export default React.memo(AddPlannedSpending);
